import { createGlobalStyle, css } from 'styled-components';
import { generateScopedStyles, generateStyles } from '@bloomberg/crescent-tokens/js/utils';
import { SCOPED_CSS_VERSION } from '../__utils__/ScopedCssContext';
import headerCss from '@bloomberg/crescent-tokens/js/header.min';
import fontFaceCss from '@bloomberg/crescent-tokens/js/font-face.min';
import sansFontFaceCss from '@bloomberg/crescent-tokens/js/font-face-sans.min';
import coreCss from '@bloomberg/crescent-tokens/js/core.min';
import sansCoreCss from '@bloomberg/crescent-tokens/js/core-sans.min';
import semanticCss from '@bloomberg/crescent-tokens/js/semantic.min';
import sansSemanticCss from '@bloomberg/crescent-tokens/js/semantic-sans.min';
import allModulesCss from '@bloomberg/crescent-tokens/js/all-modules.min';
import lightTokens from '@bloomberg/crescent-tokens/js/light/tokens';
import darkTokens from '@bloomberg/crescent-tokens/js/dark/tokens';

export const CSS_VAR_CONFIG_MAX_WIDTH = '--bbui-config-maxwidth';
const CDS_PROP_SIZING = 'data-cds-sizing';

/**
 *
 * @deprecated - do not use outside BBUI. This function will be moved to the Crescent Library as soon as it is capable
 */
export const getSizingProp = (compact?: boolean, target = '') => ({
    [CDS_PROP_SIZING]: compact === undefined ? undefined : compact ? `compact${target}` : `regular${target}`,
});

function generateThemedCss(theme: 'light' | 'dark' | 'system', sansOnly: boolean = true, scoped?: boolean) {
    const genStyles = scoped ? generateScopedStyles : generateStyles;
    return genStyles(
        theme,
        `${headerCss}\n${sansOnly ? sansFontFaceCss : fontFaceCss}`,
        lightTokens,
        darkTokens,
        sansOnly ? sansCoreCss : coreCss,
        sansOnly ? sansSemanticCss : semanticCss,
        allModulesCss
    );
}

const getCrescentCss = (activeTheme?: string, sansOnly?: boolean, scoped?: boolean) => {
    switch (activeTheme) {
        case 'light':
            return generateThemedCss('light', sansOnly, scoped);
        case 'dark':
            return generateThemedCss('dark', sansOnly, scoped);
        default:
            return generateThemedCss('system', sansOnly, scoped);
    }
};

export default createGlobalStyle<{
    maxWidth?: string;
    activeTheme?: string;
    sansOnly?: boolean;
    scoped?: boolean;
}>`
  ${({ activeTheme, sansOnly, scoped }) =>
      scoped
          ? css`
                @layer crescent.normalize {
                    *,
                    ::before,
                    ::after {
                        box-sizing: border-box;
                    }
                    body {
                        margin: 0;
                    }
                }
                @layer crescent.baseline {
                    body {
                        -webkit-font-smoothing: antialiased;
                    }
                }

                ${getCrescentCss(activeTheme, sansOnly, scoped)}
            `
          : getCrescentCss(activeTheme, sansOnly)};

  ${({ maxWidth, scoped }) =>
      maxWidth && scoped
          ? css`
                @scope ([data-cds-scoped="${SCOPED_CSS_VERSION}"]) to ([data-cds-isolate], [data-cds-scoped]) {
                    :scope {
                        ${CSS_VAR_CONFIG_MAX_WIDTH}: ${maxWidth};
                    }
                }
            `
          : maxWidth
            ? css`
                  :root {
                      ${CSS_VAR_CONFIG_MAX_WIDTH}: ${maxWidth};
                  }
              `
            : ''}
`;
